// frontend/src/components/tablekit/DataTable.js
import * as React from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Typography, IconButton, Tooltip, TablePagination, Button, Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FilterToolbar from './FilterToolbar';
import { useTableKit } from './useTableKit';
import { buildCsvBlobs } from './csv';

/** Build sx for a cell based on the column config */
function cellSx(c = {}) {
  return {
    ...(c.width ? { width: c.width } : {}),
    ...(c.minWidth ? { minWidth: c.minWidth } : {}),
    ...(c.maxWidth ? { maxWidth: c.maxWidth } : {}),
    // wrapping
    whiteSpace: c.wrap === 'nowrap' ? 'nowrap' : 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  };
}

function HeaderCell({ c, sort, setSortBy }) {
  const active = sort.key === c.key;
  const direction = active ? sort.dir : 'asc';

  const onSort = () => {
    const nextDir = active && direction === 'asc' ? 'desc' : 'asc';
    setSortBy(c.key, nextDir, c.accessor || null);
  };

  return (
    <TableCell
      sx={{ fontWeight: 700, ...cellSx(c) }}
      align={c.align || (c.type === 'number' ? 'right' : 'left')}
    >
      <TableSortLabel active={active} direction={direction} onClick={onSort}>
        {c.header ?? c.key}
      </TableSortLabel>
    </TableCell>
  );
}

/** ---------- SubTable with optional row expansion ---------- */
function SubTable({ parent, spec }) {
  const rows = Array.isArray(parent[spec.key]) ? parent[spec.key] : [];
  const instance = useTableKit({ rows, columns: spec.columns, initialRowsPerPage: 15 });

  const columns = spec.columns;
  const hasRowExpander = typeof spec.renderExpanded === 'function';

  // local expand/collapse state for subtable rows
  const [open, setOpen] = React.useState({});
  const toggle = (rowKey) => setOpen(s => ({ ...s, [rowKey]: !s[rowKey] }));

  const csvBlobs = React.useMemo(
    () => buildCsvBlobs({ title: spec.title || spec.key, columns, rows: instance.allRows }),
    [columns, instance.allRows, spec.key, spec.title]
  );

  const onExport = () => {
    for (const b of csvBlobs) {
      const blob = new Blob([b.content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Box sx={{ p: 2, pl: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">{spec.title || 'Details'}</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterToolbar
            columns={columns}
            filters={instance.filters}
            addFilter={instance.addFilter}
            updateFilter={instance.updateFilter}
            removeFilter={instance.removeFilter}
            clearFilters={instance.clearFilters}
            dense
          />
          <Tooltip title="Export CSV">
            <span>
              <Button size="small" startIcon={<DownloadIcon />} onClick={onExport}>
                Export
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'hidden' }}>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& th, & td': { py: 0.75 }
          }}
        >
          <TableHead>
            <TableRow>
              {hasRowExpander && <TableCell sx={{ width: 40 }} />}
              {columns.map((c) => (
                <HeaderCell key={c.key} c={c} sort={instance.sort} setSortBy={instance.setSortBy} />
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {instance.visibleRows.map((r, i) => {
              const rowKey = r.key ?? `${i}`;
              return (
                <React.Fragment key={`${parent.key}-${spec.key}-${rowKey}`}>
                  <TableRow hover>
                    {hasRowExpander && (
                      <TableCell padding="checkbox" sx={{ width: 40 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggle(rowKey)}
                          aria-label="Expand row"
                        >
                          {open[rowKey] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                    )}
                    {columns.map((c) => {
                      const raw = c.accessor ? c.accessor(r, parent) : r[c.key];
                      return (
                        <TableCell
                          key={c.key}
                          align={c.align || (c.type === 'number' ? 'right' : 'left')}
                          sx={cellSx(c)}
                        >
                          {c.render ? c.render(raw, r, parent) : (raw != null ? String(raw) : '—')}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {hasRowExpander && open[rowKey] && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        sx={{ p: 0 }}
                      >
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
                          {spec.renderExpanded(r, parent)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[15]}
        component="div"
        count={instance.allRows.length}
        rowsPerPage={instance.rowsPerPage}
        page={instance.page}
        onPageChange={instance.onChangePage}
      />
    </Box>
  );
}

/** ---------- Parent DataTable ---------- */
export function DataTable({
  title,
  columns = [],
  rows = [],
  subTables = [], // [{ key, title, columns, renderExpanded? }]
  dense = true,
}) {
  const instance = useTableKit({ rows, columns, initialRowsPerPage: 15 });

  const csvBlobs = React.useMemo(
    () => buildCsvBlobs({ title: title || 'table', columns, rows: instance.allRows, subTables }),
    [title, columns, instance.allRows, subTables]
  );

  const onExport = () => {
    for (const b of csvBlobs) {
      const blob = new Blob([b.content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // expanded state for all parents (if any subTables exist)
  const [open, setOpen] = React.useState({});
  const toggle = (k) => setOpen(s => ({ ...s, [k]: !s[k] }));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/* Title + Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        {title && <Typography variant="h6">{title}</Typography>}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FilterToolbar
            columns={columns}
            filters={instance.filters}
            addFilter={instance.addFilter}
            updateFilter={instance.updateFilter}
            removeFilter={instance.removeFilter}
            clearFilters={instance.clearFilters}
            dense
          />
          <Tooltip title="Export CSV">
            <span>
              <Button size="small" startIcon={<DownloadIcon />} onClick={onExport}>
                Export
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'hidden' }}>
        <Table
          size={dense ? 'small' : 'medium'}
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& th, & td': { py: dense ? 0.75 : 1 }
          }}
        >
          <TableHead>
            <TableRow>
              {/* Expand/Collapse column only if there are sub tables */}
              {subTables.length > 0 && <TableCell sx={{ width: 40 }} />}
              {columns.map((c) => (
                <HeaderCell key={c.key} c={c} sort={instance.sort} setSortBy={instance.setSortBy} />
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {instance.visibleRows.map((r, idx) => {
              const rowKey = r.key ?? `${idx}`;
              return (
                <React.Fragment key={rowKey}>
                  <TableRow hover>
                    {subTables.length > 0 && (
                      <TableCell padding="checkbox" sx={{ width: 40 }}>
                        <IconButton size="small" onClick={() => toggle(rowKey)} aria-label="Expand row">
                          {open[rowKey] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                    )}
                    {columns.map((c) => {
                      const raw = c.accessor ? c.accessor(r) : r[c.key];
                      return (
                        <TableCell
                          key={c.key}
                          align={c.align || (c.type === 'number' ? 'right' : 'left')}
                          sx={cellSx(c)}
                        >
                          {c.render ? c.render(raw, r) : (raw != null ? String(raw) : '—')}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {subTables.length > 0 && open[rowKey] && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} sx={{ p: 0 }}>
                        <Box>
                          <Divider />
                          {subTables.map((st) => (
                            <SubTable key={`${rowKey}-${st.key}`} parent={r} spec={st} />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[15]}
        component="div"
        count={instance.allRows.length}
        rowsPerPage={instance.rowsPerPage}
        page={instance.page}
        onPageChange={instance.onChangePage}
      />
    </Paper>
  );
}
